import React, { useState, useEffect, useRef } from 'react';

// Zoomable + pannable image preview. Behaves like a normal photo viewer:
//   - Scroll wheel zooms in/out (centered on the cursor)
//   - Click + drag to pan
//   - Double-click to reset
//   - + / − / Reset buttons in a floating toolbar
// The wheel handler is registered manually with { passive: false } so
// preventDefault() actually stops the page from scrolling underneath.
export default function ZoomableImage({ url, alt, width = '85vw', height = '80vh', background = '#0a0a0a' }) {
  const MIN_ZOOM = 0.2;
  const MAX_ZOOM = 10;
  const containerRef = useRef(null);
  const draggingRef = useRef(false);
  const lastPosRef = useRef({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);

  // Reset transform whenever a new image loads.
  useEffect(() => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  }, [url]);

  // Wheel zoom — manual addEventListener because React's synthetic wheel
  // listener is passive by default (preventDefault has no effect there).
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onWheel = (e) => {
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      const cx = e.clientX - rect.left - rect.width / 2;
      const cy = e.clientY - rect.top - rect.height / 2;
      const factor = e.deltaY < 0 ? 1.15 : 1 / 1.15;
      setZoom(prev => {
        const next = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, prev * factor));
        // Keep the point under the cursor stationary while zooming.
        const ratio = next / prev;
        setPan(p => ({
          x: cx - (cx - p.x) * ratio,
          y: cy - (cy - p.y) * ratio
        }));
        return next;
      });
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  const startDrag = (e) => {
    if (e.button !== 0) return;
    draggingRef.current = true;
    setDragging(true);
    lastPosRef.current = { x: e.clientX, y: e.clientY };
  };
  const onDrag = (e) => {
    if (!draggingRef.current) return;
    const dx = e.clientX - lastPosRef.current.x;
    const dy = e.clientY - lastPosRef.current.y;
    lastPosRef.current = { x: e.clientX, y: e.clientY };
    setPan(p => ({ x: p.x + dx, y: p.y + dy }));
  };
  const endDrag = () => {
    draggingRef.current = false;
    setDragging(false);
  };

  const reset = () => { setZoom(1); setPan({ x: 0, y: 0 }); };
  const zoomIn  = () => setZoom(z => Math.min(MAX_ZOOM, z * 1.25));
  const zoomOut = () => setZoom(z => Math.max(MIN_ZOOM, z / 1.25));

  const btnStyle = {
    background: 'rgba(255,255,255,0.18)', color: 'white',
    border: '1px solid rgba(255,255,255,0.3)', borderRadius: 4,
    width: 30, height: 26, cursor: 'pointer', fontWeight: 700, fontSize: 14
  };

  return (
    <div
      ref={containerRef}
      onMouseDown={startDrag}
      onMouseMove={onDrag}
      onMouseUp={endDrag}
      onMouseLeave={endDrag}
      onDoubleClick={reset}
      style={{
        position: 'relative',
        width,
        height,
        overflow: 'hidden',
        background,
        borderRadius: 8,
        cursor: dragging ? 'grabbing' : 'grab',
        userSelect: 'none'
      }}
    >
      <img
        src={url}
        alt={alt}
        draggable={false}
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          maxWidth: width,
          maxHeight: height,
          transform: `translate(-50%, -50%) translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
          transformOrigin: 'center center',
          transition: dragging ? 'none' : 'transform 0.08s ease-out',
          pointerEvents: 'none'
        }}
      />
      {/* Floating zoom toolbar */}
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'absolute',
          bottom: 12,
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          background: 'rgba(0,0,0,0.7)',
          padding: '5px 8px',
          borderRadius: 8,
          backdropFilter: 'blur(4px)'
        }}
      >
        <button onClick={zoomOut} title="Zoom out" style={btnStyle}>−</button>
        <span style={{ color: 'white', fontSize: 12, minWidth: 48, textAlign: 'center', fontWeight: 600 }}>
          {Math.round(zoom * 100)}%
        </span>
        <button onClick={zoomIn} title="Zoom in" style={btnStyle}>+</button>
        <button onClick={reset} title="Reset (or double-click image)"
          style={{ ...btnStyle, width: 'auto', padding: '0 10px' }}>Reset</button>
      </div>
      {/* Hint */}
      <div style={{
        position: 'absolute', top: 12, left: '50%', transform: 'translateX(-50%)',
        color: 'rgba(255,255,255,0.55)', fontSize: 11, background: 'rgba(0,0,0,0.4)',
        padding: '3px 10px', borderRadius: 4, pointerEvents: 'none', whiteSpace: 'nowrap'
      }}>
        Scroll = zoom · Drag = pan · Double-click = reset
      </div>
    </div>
  );
}
